import { effect, inject, Injectable, linkedSignal } from '@angular/core';
import { NgRxEffectsBase } from '@shared/utility/NgRxUtils';
import { createEffect, ofType } from '@ngrx/effects';
import { exhaustMap, filter, map, mergeMap, take, tap } from 'rxjs';
import { CoreDefaultActions } from '@core/state/core.actions';
import { GLOBAL_STATE_BROADCAST_DI_TOKEN } from '@core/broadcast/broadcast';
import { BroadcastMessageType } from '@core/broadcast/broadcast.model';
import { toSignal } from '@angular/core/rxjs-interop';
import { translate } from '@jsverse/transloco';
import { AuthApi } from '@core/auth/services/auth-api';
import { AppState } from '@core/state/services/app-state';

/** Chiave con cui il popup di sessione scaduta viene chiuso quando la sessione torna valida. */
const SESSION_EXPIRED_POPUP_KEY = 'session-expired';

@Injectable({ providedIn: 'any' })
export class CoreEffects extends NgRxEffectsBase {
  private readonly authService = inject(AuthApi);
  private readonly globalBroadcastService = inject(GLOBAL_STATE_BROADCAST_DI_TOKEN);
  private readonly globalBroadcastMessages = toSignal(
    this.globalBroadcastService.messagesOfType(BroadcastMessageType.DISPATCH_ACTION),
    { initialValue: null }
  );
  private readonly globalBroadcastMessagesToPublish = linkedSignal(() => this.globalBroadcastMessages());
  private readonly sessionExpired = inject(AppState).sessionExpired;

  public checkIfValidBeforeDispatch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CoreDefaultActions.checkValidBeforeDispatch),
      tap(({ valid, alertMsg }) => {
        if (!valid) {
          this.alertService.showAlert(null, alertMsg, false);
        }
      }),
      mergeMap(({ valid, actions }) => (valid ? actions : [CoreDefaultActions.checkValidBeforeDispatchFailure()]))
    )
  );

  public canDestroyHandler$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CoreDefaultActions.canDestroyHandler),
      exhaustMap(({ obs$, actions }) =>
        obs$.pipe(
          take(1),
          filter((res) => res),
          mergeMap(() => actions)
        )
      )
    )
  );

  public globalBroadCastActionEmitter$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CoreDefaultActions.dispatchActionAndPublishMessageToBroadcastChannel),
      mergeMap(({ actions }) => {
        actions.forEach((action) => {
          this.globalBroadcastService.publish({ type: BroadcastMessageType.DISPATCH_ACTION, payload: action });
        });
        return actions;
      })
    )
  );

  public showAlertMessage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CoreDefaultActions.showAlertMessage),
        map(({ title, message, isError }) => {
          this.alertService.showAlert(title, message, isError);
        })
      ),
    { dispatch: false }
  );

  public confirmThenDoAction$ = this.ngrxUtils.showConfirmThenDispatchEffect(CoreDefaultActions.confirmThenDoAction);

  constructor() {
    super();

    effect(() => {
      const payload = this.globalBroadcastMessagesToPublish()?.payload;
      if (payload && 'type' in payload) {
        this.store.dispatch(payload);
        this.globalBroadcastMessagesToPublish.set(null);
      }
    });

    /*
      Un rientro riuscito riporta `sessionExpired` a `false` mentre il popup è ancora a video: lasciarlo lì
      darebbe da leggere «sessione scaduta» davanti a un'applicazione viva, e chi si disconnettesse perderebbe
      il lavoro senza motivo.
    */
    effect(() => {
      if (!this.sessionExpired()) {
        this.alertService.dismiss(SESSION_EXPIRED_POPUP_KEY);
        return;
      }

      const [title, msg, yes, no] = translate(['attention', 'sessionExpiredMsg', 'continued', 'disconnect']);
      const onUserChoice = (staySignedIn: boolean): void => {
        if (!staySignedIn) {
          this.authService.logout(false);
        }
      };
      this.alertService.showConfirm(title, msg, yes, no, onUserChoice, SESSION_EXPIRED_POPUP_KEY);
    });
  }
}
